// Karty UNO kreslené kódem (SVG). Kvůli CSP žádné atributy style, jen prezentační atributy.
// Každá barva má v rohu i tvar (kolečko, trojúhelník, čtverec, kosočtverec), aby šla rozlišit i bez barev.
import type { Card, Color } from "../../../games/uno/meta.ts";

export const FILL: Record<Color | "k", string> = { r: "#d32f2f", y: "#f9c80e", g: "#2e9e44", b: "#1e6fd9", k: "#222" };
const INK: Record<Color | "k", string> = { r: "#d32f2f", y: "#b08800", g: "#23803a", b: "#1e6fd9", k: "#222" };

function shape(color: Color, x: number, y: number, r: number, fill: string): string {
  switch (color) {
    case "r":
      return `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}"/>`;
    case "y":
      return `<polygon points="${x},${y - r} ${x + r},${y + r * 0.8} ${x - r},${y + r * 0.8}" fill="${fill}"/>`;
    case "g":
      return `<rect x="${x - r * 0.85}" y="${y - r * 0.85}" width="${r * 1.7}" height="${r * 1.7}" fill="${fill}"/>`;
    case "b":
      return `<polygon points="${x},${y - r} ${x + r},${y} ${x},${y + r} ${x - r},${y}" fill="${fill}"/>`;
  }
}

function skipSymbol(x: number, y: number, r: number, color: string, stroke: number): string {
  const d = r * 0.7;
  return `<circle cx="${x}" cy="${y}" r="${r}" fill="none" stroke="${color}" stroke-width="${stroke}"/><line x1="${x - d}" y1="${y + d}" x2="${x + d}" y2="${y - d}" stroke="${color}" stroke-width="${stroke}" stroke-linecap="round"/>`;
}

function reverseSymbol(x: number, y: number, s: number, color: string): string {
  // Dvě protisměrné šipky.
  const w = s * 1.2;
  const h = s * 0.32;
  const arrow = (dx: number, dy: number, dir: 1 | -1) => {
    const x0 = x - (w / 2) * dir + dx;
    const x1 = x + (w / 2) * dir + dx;
    const yy = y + dy;
    return `<path d="M${x0} ${yy} L${x1 - dir * h} ${yy}" stroke="${color}" stroke-width="${h * 0.8}" stroke-linecap="round"/><polygon points="${x1},${yy} ${x1 - dir * h * 1.6},${yy - h * 1.2} ${x1 - dir * h * 1.6},${yy + h * 1.2}" fill="${color}"/>`;
  };
  return arrow(0, -s * 0.35, 1) + arrow(0, s * 0.35, -1);
}

function quarters(cx: number, cy: number, rx: number, ry: number): string {
  // Čtyřbarevný ovál černé karty.
  const parts: [string, number][] = [["r", 180], ["y", 270], ["g", 0], ["b", 90]];
  return parts
    .map(([c, start]) => {
      const a0 = (start * Math.PI) / 180;
      const a1 = ((start + 90) * Math.PI) / 180;
      const p0 = `${cx + rx * Math.cos(a0)},${cy + ry * Math.sin(a0)}`;
      const p1 = `${cx + rx * Math.cos(a1)},${cy + ry * Math.sin(a1)}`;
      return `<path d="M${cx},${cy} L${p0} A${rx},${ry} 0 0 1 ${p1} Z" fill="${FILL[c as Color]}"/>`;
    })
    .join("");
}

function label(card: Card): string {
  if (card.kind === "num") return String(card.value);
  if (card.kind === "draw2") return "+2";
  if (card.kind === "wild4") return "+4";
  return "";
}

/** SVG líce karty, viewBox 0 0 100 150. */
export function cardSvg(card: Card): string {
  const c = card.color ?? "k";
  const bg = FILL[c];
  const ink = INK[c];
  const text = label(card);
  let center = "";
  if (card.color === null) {
    center = quarters(50, 75, 30, 44);
    if (text) center += `<text x="50" y="92" font-size="44" font-weight="900" text-anchor="middle" fill="#fff" stroke="#222" stroke-width="3" paint-order="stroke">${text}</text>`;
  } else if (card.kind === "skip") center = skipSymbol(50, 75, 22, ink, 9);
  else if (card.kind === "reverse") center = reverseSymbol(50, 75, 40, ink);
  else center = `<text x="50" y="93" font-size="${text.length > 1 ? 46 : 58}" font-weight="900" text-anchor="middle" fill="${ink}">${text}</text>`;

  const cornerText = card.kind === "skip" ? "⊘" : card.kind === "reverse" ? "⇄" : text;
  const corner = card.color
    ? shape(card.color, 15, 16, 7, "#fff") + (cornerText ? `<text x="85" y="22" font-size="17" font-weight="800" text-anchor="middle" fill="#fff">${cornerText}</text>` : "")
    : cornerText
      ? `<text x="22" y="24" font-size="16" font-weight="800" text-anchor="middle" fill="#fff">${cornerText}</text>`
      : "";
  return `<svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
<rect x="2" y="2" width="96" height="146" rx="12" fill="#fff"/>
<rect x="7" y="7" width="86" height="136" rx="9" fill="${bg}"/>
<ellipse cx="50" cy="75" rx="36" ry="54" fill="#fff" transform="rotate(20 50 75)"/>
${center}${corner}</svg>`;
}

/** Rub karty (vlastní neutrální vzor, ne logo originálu). */
export function backSvg(): string {
  const dots = (["r", "y", "g", "b"] as const)
    .map((c, i) => `<circle cx="${i % 2 ? 64 : 36}" cy="${i < 2 ? 61 : 89}" r="12" fill="${FILL[c]}"/>`)
    .join("");
  return `<svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
<rect x="2" y="2" width="96" height="146" rx="12" fill="#fff"/>
<rect x="7" y="7" width="86" height="136" rx="9" fill="#2d2d3a"/>
<ellipse cx="50" cy="75" rx="34" ry="52" fill="#fff" opacity="0.12" transform="rotate(20 50 75)"/>
${dots}</svg>`;
}
